/**
 * High-level item operations shared by the web client and the Durable Object.
 * Every op runs in one Yjs transaction with the caller's origin, so local undo
 * (SPEC §6.2) and server audit batches (SPEC §6.5) see it as one unit.
 */
import * as Y from 'yjs'
import { addDays, zonedTimeToEpoch } from './dates'
import {
  applyTextDiff,
  cleanIcon,
  createYItem,
  ensureAttachments,
  ensureCompletions,
  ensureTags,
  getYItem,
  itemsMap,
  normalizeTag,
  readTreeNode,
  setKey,
  setProp,
  ensureProps,
  fieldsMap,
  setTags,
  touch,
  yBody,
  yProps,
  type ItemInit,
  type YItem,
} from './doc'
import { isEq, remapStored, type Ref } from './math'
import { fieldKeyFromLabel } from './fields'
import { orderBetween, ordersBetween } from './order'
import { alignRecurrence, nextOccurrence, skipOccurrence } from './recurrence'
import { descendantsOf, resolveTree, wouldCreateCycle, type ResolvedTree } from './tree'
import { INBOX_ID, SYSTEM_IDS, type ActorId, type Attachment, type FieldDef, type Recurrence, type Reminder, type ReminderKind, type Tint, type UserId } from './types'

export interface OpContext {
  doc: Y.Doc
  actor: ActorId
  /** Household-local today (YYYY-MM-DD). */
  today: string
  /** Household IANA timezone (for reminders). */
  tz: string
  /** Transaction origin (local-undo tracking, server batch attribution). */
  origin?: unknown
  now?: () => number
  /** Optional cached tree; recomputed when absent or stale. */
  tree?: ResolvedTree
}

export class OpError extends Error {
  constructor(
    public code: 'not_found' | 'system_item' | 'cycle' | 'invalid',
    message: string,
  ) {
    super(message)
  }
}

const nowOf = (ctx: OpContext) => (ctx.now ? ctx.now() : Date.now())

export function computeTree(doc: Y.Doc): ResolvedTree {
  const nodes = new Map<string, ReturnType<typeof readTreeNode>>()
  itemsMap(doc).forEach((y, id) => {
    if (y instanceof Y.Map) nodes.set(id, readTreeNode(id, y))
  })
  return resolveTree(nodes)
}

function tx<T>(ctx: OpContext, fn: () => T): T {
  let out!: T
  ctx.doc.transact(() => {
    out = fn()
  }, ctx.origin ?? null)
  return out
}

function must(ctx: OpContext, id: string): YItem {
  const y = getYItem(ctx.doc, id)
  if (!y) throw new OpError('not_found', `No item with id ${id}`)
  return y
}

function orderOf(ctx: OpContext, id: string): string | null {
  const y = getYItem(ctx.doc, id)
  return y ? ((y.get('order') as string) ?? null) : null
}

function freshTree(ctx: OpContext): ResolvedTree {
  // Callers inside one op may mutate structure; recompute lazily when needed.
  return computeTree(ctx.doc)
}

export interface Position {
  parentId: string | null
  /** Insert right after this sibling. */
  afterId?: string | null
  /** Insert right before this sibling. */
  beforeId?: string | null
  /** When neither after/before is given. Default 'end'. */
  at?: 'start' | 'end'
}

/** Compute an order key for a position among the parent's children (including hidden). */
export function orderForPosition(ctx: OpContext, pos: Position, tree?: ResolvedTree, excludeId?: string): string {
  const t = tree ?? ctx.tree ?? freshTree(ctx)
  const siblings = (t.childrenOf.get(pos.parentId) ?? []).filter((s) => s !== excludeId)
  if (pos.afterId !== undefined && pos.afterId !== null) {
    const i = siblings.indexOf(pos.afterId)
    const next = i >= 0 ? siblings[i + 1] : undefined
    return orderBetween(orderOf(ctx, pos.afterId), next ? orderOf(ctx, next) : null)
  }
  if (pos.beforeId !== undefined && pos.beforeId !== null) {
    const i = siblings.indexOf(pos.beforeId)
    const prev = i > 0 ? siblings[i - 1] : undefined
    return orderBetween(prev ? orderOf(ctx, prev) : null, orderOf(ctx, pos.beforeId))
  }
  if (pos.at === 'start') {
    const first = siblings[0]
    return orderBetween(null, first ? orderOf(ctx, first) : null)
  }
  const last = siblings[siblings.length - 1]
  return orderBetween(last ? orderOf(ctx, last) : null, null)
}

// ---------------------------------------------------------------------------
// Create

export type AddInit = Omit<ItemInit, 'parentId' | 'order'> & Position

export function addItem(ctx: OpContext, init: AddInit): string {
  return tx(ctx, () => {
    const { parentId, afterId, beforeId, at, ...rest } = init
    const parent = parentId ?? null
    if (parent !== null && !getYItem(ctx.doc, parent)) throw new OpError('not_found', `No parent ${parent}`)
    const order = orderForPosition(ctx, { parentId: parent, afterId, beforeId, at })
    const now = nowOf(ctx)
    const y = createYItem(ctx.doc, { ...rest, parentId: parent, order }, ctx.actor, now)
    if (rest.remind?.length || rest.date) recomputeReminders(y, ctx.tz, now)
    ctx.tree = undefined
    return y.get('id') as string
  })
}

/** Create several sibling rows in order (multi-line paste). Returns new ids. */
export function addItems(ctx: OpContext, pos: Position, titles: string[], extra: Omit<ItemInit, 'title'> = {}): string[] {
  return tx(ctx, () => {
    const tree = ctx.tree ?? freshTree(ctx)
    const siblings = tree.childrenOf.get(pos.parentId) ?? []
    let lo: string | null = null
    let hi: string | null = null
    if (pos.afterId) {
      const i = siblings.indexOf(pos.afterId)
      lo = orderOf(ctx, pos.afterId)
      hi = i >= 0 && siblings[i + 1] ? orderOf(ctx, siblings[i + 1]!) : null
    } else if (pos.beforeId) {
      const i = siblings.indexOf(pos.beforeId)
      hi = orderOf(ctx, pos.beforeId)
      lo = i > 0 ? orderOf(ctx, siblings[i - 1]!) : null
    } else if (pos.at === 'start') {
      hi = siblings[0] ? orderOf(ctx, siblings[0]) : null
    } else {
      lo = siblings.length ? orderOf(ctx, siblings[siblings.length - 1]!) : null
    }
    const orders = ordersBetween(lo, hi, titles.length)
    const now = nowOf(ctx)
    const ids = titles.map((title, i) => {
      const y = createYItem(ctx.doc, { ...extra, title, parentId: pos.parentId, order: orders[i]! }, ctx.actor, now)
      if (extra.remind?.length || extra.date) recomputeReminders(y, ctx.tz, now)
      return y.get('id') as string
    })
    ctx.tree = undefined
    return ids
  })
}

// ---------------------------------------------------------------------------
// Structure

/** Move an item. Refuses cycles and re-parenting system items (SPEC §5.2, §5.6). */
export function moveItem(ctx: OpContext, id: string, pos: Position): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    const tree = ctx.tree ?? freshTree(ctx)
    const newParent = pos.parentId ?? null
    if (SYSTEM_IDS.has(id) || y.get('system')) {
      if (newParent !== null) throw new OpError('system_item', 'System items stay at the top level')
    }
    if (newParent !== null && !getYItem(ctx.doc, newParent)) throw new OpError('not_found', `No parent ${newParent}`)
    if (wouldCreateCycle(tree, id, newParent)) throw new OpError('cycle', 'Cannot move an item under its own descendant')
    const order = orderForPosition(ctx, { ...pos, parentId: newParent }, tree, id)
    setKey(y, 'parentId', newParent)
    setKey(y, 'order', order)
    touch(y, ctx.actor, nowOf(ctx))
    ctx.tree = undefined
  })
}

/**
 * Pages (sidebar tree): `on` marks the item as a page and, when `pos` is given, moves it
 * there (top level or under another page). Turning it off puts it back on the board
 * unless `pos` says otherwise.
 */
export function setPage(ctx: OpContext, id: string, on: boolean, pos?: Position): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    if (SYSTEM_IDS.has(id) || y.get('system')) throw new OpError('system_item', 'The Inbox and Agent log stay on the board')
    if (on && pos?.parentId) {
      const parent = getYItem(ctx.doc, pos.parentId)
      if (!parent || parent.get('page') !== true) throw new OpError('invalid', 'Subpages go under another page')
    }
    const target = pos ?? (on ? undefined : { parentId: null, at: 'start' as const })
    if (target) moveItem(ctx, id, target)
    setKey(y, 'page', on || undefined)
    touch(y, ctx.actor, nowOf(ctx))
  })
}

/** Indent: become the last child of `newParentId` (the previous visible sibling). */
export function indentItem(ctx: OpContext, id: string, newParentId: string): void {
  moveItem(ctx, id, { parentId: newParentId, at: 'end' })
}

/** Outdent: become the sibling right after the current parent. */
export function outdentItem(ctx: OpContext, id: string): boolean {
  const tree = ctx.tree ?? freshTree(ctx)
  const parent = tree.parentOf.get(id) ?? null
  if (parent === null) return false
  const grand = tree.parentOf.get(parent) ?? null
  moveItem(ctx, id, { parentId: grand, afterId: parent })
  return true
}

// ---------------------------------------------------------------------------
// Completion (SPEC §5.5, §7.4)

/**
 * `resting`: a repeating task checked off for this time, done until `next` (its next time).
 * `recurred`: its next time is already here (an overdue one caught up), so it's open again now.
 */
export type DoneResult = { kind: 'done' } | { kind: 'undone' } | { kind: 'recurred'; next: string } | { kind: 'resting'; next: string } | { kind: 'noop' }

export function setDone(ctx: OpContext, id: string, done: boolean): DoneResult {
  return tx(ctx, () => {
    const y = must(ctx, id)
    const now = nowOf(ctx)
    const cur = y.get('done')
    if (done) {
      const recur = y.get('recur') as Recurrence | undefined
      const date = y.get('date') as string | undefined
      if (recur && date) {
        if (cur === true) return { kind: 'noop' } as DoneResult
        ensureCompletions(y).push([{ at: now, by: ctx.actor, date }])
        const next = nextOccurrence(recur, date, ctx.today)
        if (next && next <= ctx.today) {
          // Its next time is already here: open again, on that day.
          shiftDate(y, next, ctx.tz, nowOf(ctx))
          if (cur !== false) y.set('done', false)
          touch(y, ctx.actor, now)
          return { kind: 'recurred', next } as DoneResult
        }
        if (next) {
          // Done for this time; it comes back on its next date (reopenRecurring).
          y.set('done', true)
          y.set('completedAt', now)
          y.set('completedBy', ctx.actor)
          y.set('reopensOn', next)
          touch(y, ctx.actor, now)
          return { kind: 'resting', next } as DoneResult
        }
        // Rule ended: fall through to a normal completion (the log entry is already written).
        y.set('done', true)
        y.set('completedAt', now)
        y.set('completedBy', ctx.actor)
        touch(y, ctx.actor, now)
        return { kind: 'done' } as DoneResult
      }
      if (cur === true) return { kind: 'noop' } as DoneResult
      y.set('done', true)
      y.set('completedAt', now)
      y.set('completedBy', ctx.actor)
      ensureCompletions(y).push([date ? { at: now, by: ctx.actor, date } : { at: now, by: ctx.actor }])
      touch(y, ctx.actor, now)
      return { kind: 'done' } as DoneResult
    }
    if (cur !== true) return { kind: 'noop' } as DoneResult
    if (y.get('reopensOn')) {
      // Unchecking a repeating task that's done for this time: that completion didn't happen.
      setKey(y, 'reopensOn', undefined)
      const comps = ensureCompletions(y)
      const arr = comps.toArray()
      const last = arr.reduce((best, c, i) => (best < 0 || c.at > arr[best]!.at ? i : best), -1)
      if (last >= 0) comps.delete(last, 1)
    }
    y.set('done', false)
    setKey(y, 'completedAt', undefined)
    setKey(y, 'completedBy', undefined)
    // Unchecking an archived done row brings it back (SPEC §7.4).
    setKey(y, 'archivedAt', undefined)
    touch(y, ctx.actor, now)
    return { kind: 'undone' } as DoneResult
  })
}

/**
 * Repeating tasks done for this time whose next time has come (`reopensOn` ≤ today): open
 * again, dated that day. Run by the server's alarm (as 'system', so it isn't anyone's activity).
 */
export function reopenRecurring(ctx: OpContext): string[] {
  const due: string[] = []
  itemsMap(ctx.doc).forEach((y, id) => {
    const on = y.get('reopensOn')
    if (typeof on === 'string' && on <= ctx.today && y.get('done') === true) due.push(id)
  })
  if (!due.length) return due
  tx(ctx, () => {
    for (const id of due) {
      const y = must(ctx, id)
      shiftDate(y, y.get('reopensOn') as string, ctx.tz, nowOf(ctx))
      setKey(y, 'reopensOn', undefined)
      y.set('done', false)
      setKey(y, 'completedAt', undefined)
      setKey(y, 'completedBy', undefined)
      touch(y, ctx.actor, nowOf(ctx))
    }
  })
  return due
}

/** "Skip this one": advance a recurring item's date without logging a completion. */
export function skipRecurrence(ctx: OpContext, id: string): string | null {
  return tx(ctx, () => {
    const y = must(ctx, id)
    const recur = y.get('recur') as Recurrence | undefined
    const date = y.get('date') as string | undefined
    if (!recur || !date) return null
    // Done for this time: skip the time it would come back.
    const resting = y.get('reopensOn') as string | undefined
    if (resting) {
      const later = skipOccurrence(recur, resting, ctx.today)
      if (!later) return null
      y.set('reopensOn', later)
      touch(y, ctx.actor, nowOf(ctx))
      return later
    }
    const next = skipOccurrence(recur, date, ctx.today)
    if (!next) return null
    shiftDate(y, next, ctx.tz, nowOf(ctx))
    touch(y, ctx.actor, nowOf(ctx))
    return next
  })
}

/** Remove one completion log entry of a recurring item and restore its date (Day-card uncheck). */
export function undoRecurringCompletion(ctx: OpContext, id: string, at: number): boolean {
  return tx(ctx, () => {
    const y = must(ctx, id)
    const comps = ensureCompletions(y)
    const arr = comps.toArray()
    const idx = arr.findIndex((c) => c.at === at)
    if (idx < 0) return false
    const entry = arr[idx]!
    comps.delete(idx, 1)
    if (entry.date) shiftDate(y, entry.date, ctx.tz, nowOf(ctx))
    // It was resting after that completion: open again.
    if (y.get('reopensOn') && y.get('done') === true) {
      setKey(y, 'reopensOn', undefined)
      y.set('done', false)
      setKey(y, 'completedAt', undefined)
      setKey(y, 'completedBy', undefined)
    }
    touch(y, ctx.actor, nowOf(ctx))
    return true
  })
}

/** Turn the checkbox on (task) or off (note/event) — SPEC §5.1. */
export function setCheckbox(ctx: OpContext, id: string, on: boolean): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    const cur = y.get('done')
    if (on && (cur === null || cur === undefined)) y.set('done', false)
    else if (!on && cur !== null) {
      y.set('done', null)
      setKey(y, 'completedAt', undefined)
      setKey(y, 'completedBy', undefined)
    } else return
    touch(y, ctx.actor, nowOf(ctx))
  })
}

// ---------------------------------------------------------------------------
// Dates & reminders

export function reminderAt(kind: ReminderKind, date: string, time: string | undefined, tz: string): number | null {
  if (kind === 'at') return time ? zonedTimeToEpoch(date, time, tz) : null
  if (kind === 'morning') return zonedTimeToEpoch(date, '08:00', tz)
  return zonedTimeToEpoch(addDays(date, -1), '18:00', tz)
}

/** Recompute `remind[].at` from each entry's kind after date/time changes. */
export function recomputeReminders(y: YItem, tz: string, now: number = Date.now()) {
  const list = y.get('remind') as Reminder[] | undefined
  if (!list?.length) return
  const date = y.get('date') as string | undefined
  const time = y.get('time') as string | undefined
  if (!date) {
    y.delete('remind')
    return
  }
  const next: Reminder[] = []
  for (const r of list) {
    if (!r.kind) {
      next.push(r)
      continue
    }
    const at = reminderAt(r.kind, date, time, tz)
    if (at === null) continue
    next.push(at === r.at ? r : { kind: r.kind, at, ...(at <= now ? { sent: true } : {}) })
  }
  if (JSON.stringify(next) !== JSON.stringify(list)) {
    if (next.length) y.set('remind', next)
    else y.delete('remind')
  }
}

function shiftDate(y: YItem, next: string, tz: string, now: number) {
  y.set('date', next)
  recomputeReminders(y, tz, now)
}

export interface DatePatch {
  date?: string | null
  time?: string | null
  endTime?: string | null
}

/**
 * Set/clear date & time. Clearing the date also clears recurrence (a recurring item MUST have a
 * date). With `alignRepeat` (the date picker), a repeat follows the new date's weekday / day of
 * the month; moves that only shift one occurrence (Days drags, leftovers) leave it alone.
 */
export function setSchedule(ctx: OpContext, id: string, patch: DatePatch, opts: { alignRepeat?: boolean } = {}): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    if (patch.date !== undefined) {
      if (patch.date === null) {
        for (const k of ['date', 'time', 'endTime', 'recur', 'remind', 'dayOrder']) setKey(y, k, undefined)
      } else {
        const prev = y.get('date') as string | undefined
        setKey(y, 'date', patch.date)
        const recur = y.get('recur') as Recurrence | undefined
        if (opts.alignRepeat && recur) {
          const aligned = alignRecurrence(recur, prev, patch.date)
          if (aligned !== recur) setKey(y, 'recur', aligned)
        }
      }
    }
    if (patch.time !== undefined) setKey(y, 'time', patch.time ?? undefined)
    if (patch.endTime !== undefined) setKey(y, 'endTime', patch.endTime ?? undefined)
    recomputeReminders(y, ctx.tz, nowOf(ctx))
    touch(y, ctx.actor, nowOf(ctx))
  })
}

/** Drop onto a Day card: sets date + dayOrder, leaves parentId alone (SPEC §7.2). */
export function placeOnDay(ctx: OpContext, id: string, date: string, neighbours: { beforeDayOrder?: string | null; afterDayOrder?: string | null } = {}): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    setKey(y, 'date', date)
    setKey(y, 'dayOrder', orderBetween(neighbours.afterDayOrder ?? null, neighbours.beforeDayOrder ?? null))
    recomputeReminders(y, ctx.tz, nowOf(ctx))
    touch(y, ctx.actor, nowOf(ctx))
  })
}

export function setRecurrence(ctx: OpContext, id: string, recur: Recurrence | null): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    if (recur && !y.get('date')) y.set('date', ctx.today)
    setKey(y, 'recur', recur ?? undefined)
    // No repeat any more: a task done for this time just stays done.
    if (!recur) setKey(y, 'reopensOn', undefined)
    if (recur && y.get('done') === null) y.set('done', false)
    touch(y, ctx.actor, nowOf(ctx))
  })
}

export function setReminders(ctx: OpContext, id: string, kinds: ReminderKind[]): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    const date = y.get('date') as string | undefined
    const time = y.get('time') as string | undefined
    if (!date) {
      setKey(y, 'remind', undefined)
      return
    }
    const now = nowOf(ctx)
    const list: Reminder[] = []
    for (const kind of kinds) {
      const at = reminderAt(kind, date, time, ctx.tz)
      if (at !== null) list.push({ kind, at, ...(at <= now ? { sent: true } : {}) })
    }
    setKey(y, 'remind', list.length ? list : undefined)
    touch(y, ctx.actor, now)
  })
}

/** Leftovers bulk move helpers (SPEC §7.2). */
export function moveDates(ctx: OpContext, moves: Array<{ id: string; date: string }>): void {
  tx(ctx, () => {
    const now = nowOf(ctx)
    for (const m of moves) {
      const y = getYItem(ctx.doc, m.id)
      if (!y) continue
      setKey(y, 'date', m.date)
      recomputeReminders(y, ctx.tz, now)
      touch(y, ctx.actor, now)
    }
  })
}

// ---------------------------------------------------------------------------
// Lifecycle: archive, trash, restore, purge

export function archiveItems(ctx: OpContext, ids: string[]): void {
  tx(ctx, () => {
    const now = nowOf(ctx)
    for (const id of ids) {
      if (SYSTEM_IDS.has(id)) continue
      const y = getYItem(ctx.doc, id)
      if (!y || y.get('archivedAt')) continue
      y.set('archivedAt', now)
      touch(y, ctx.actor, now)
    }
  })
}

export function unarchiveItem(ctx: OpContext, id: string): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    setKey(y, 'archivedAt', undefined)
    touch(y, ctx.actor, nowOf(ctx))
  })
}

/**
 * Trash an item and its (not yet trashed) descendants with one timestamp, so
 * restore brings back exactly that subtree, and anything concurrently added under
 * a trashed parent shows up in the Inbox instead of vanishing (SPEC §5.2).
 */
export function trashItem(ctx: OpContext, id: string): void {
  tx(ctx, () => {
    if (SYSTEM_IDS.has(id)) throw new OpError('system_item', 'System items cannot be trashed')
    const y = must(ctx, id)
    if (y.get('system')) throw new OpError('system_item', 'System items cannot be trashed')
    const tree = ctx.tree ?? freshTree(ctx)
    const now = nowOf(ctx)
    if (!y.get('trashedAt')) {
      y.set('trashedAt', now)
      touch(y, ctx.actor, now)
    }
    for (const d of descendantsOf(tree, id)) {
      const dy = getYItem(ctx.doc, d)
      if (dy && !dy.get('trashedAt')) {
        dy.set('trashedAt', now)
        touch(dy, ctx.actor, now)
      }
    }
    ctx.tree = undefined
  })
}

export function restoreItem(ctx: OpContext, id: string): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    const stamp = y.get('trashedAt') as number | undefined
    const tree = ctx.tree ?? freshTree(ctx)
    const now = nowOf(ctx)
    if (stamp) {
      for (const d of descendantsOf(tree, id)) {
        const dy = getYItem(ctx.doc, d)
        if (dy && dy.get('trashedAt') === stamp) {
          dy.delete('trashedAt')
          touch(dy, ctx.actor, now)
        }
      }
    }
    setKey(y, 'trashedAt', undefined)
    // If the parent is gone or still in the trash, bring it back to the Inbox.
    const pid = y.get('parentId') as string | null
    if (pid) {
      const p = getYItem(ctx.doc, pid)
      if (!p || p.get('trashedAt')) {
        y.set('parentId', getYItem(ctx.doc, INBOX_ID) ? INBOX_ID : null)
        y.set('order', orderForPosition(ctx, { parentId: y.get('parentId') as string | null, at: 'start' }, freshTree(ctx), id))
      }
    }
    touch(y, ctx.actor, now)
    ctx.tree = undefined
  })
}

/** Permanently delete (purge) an item and its descendants from the doc. */
export function purgeItem(ctx: OpContext, id: string): number {
  return tx(ctx, () => {
    if (SYSTEM_IDS.has(id)) return 0
    const tree = ctx.tree ?? freshTree(ctx)
    const ids = [id, ...descendantsOf(tree, id)]
    const items = itemsMap(ctx.doc)
    let n = 0
    for (const d of ids) {
      if (SYSTEM_IDS.has(d)) continue
      if (items.has(d)) {
        items.delete(d)
        n++
      }
    }
    ctx.tree = undefined
    return n
  })
}

// ---------------------------------------------------------------------------
// Simple field patches

export interface ItemPatch {
  title?: string
  done?: boolean | null
  assignee?: UserId | null
  pinned?: boolean
  page?: boolean
  doneStyle?: import('./types').DoneStyle
  tint?: Tint
  /** An emoji icon; null or '' removes it (see cleanIcon). */
  icon?: string | null
  coverId?: string | null
  shareToken?: string | null
  childFields?: string[] | null
  /** Fields shown under the rows inside it ([] = none); null = back to the default. */
  rowFields?: string[] | null
  /** The order of its page's parts; null = not arranged (a card's, or automatic). */
  pageOrder?: import('./page-order').PageSection[] | null
  /** The same for everything inside it; null = not arranged. */
  childPageOrder?: import('./page-order').PageSection[] | null
  labels?: Record<string, string> | null
  link?: import('./types').LinkPreview | null
  tags?: string[]
  addTags?: string[]
  removeTags?: string[]
  props?: Record<string, unknown>
  /** Equations for everything inside it (SPEC §7.12), by detail key; null takes one off. */
  calcInside?: Record<string, string | null>
  /** The detail its bar shows as its total; null = the automatic one. */
  total?: string | null
}

export function patchItem(ctx: OpContext, id: string, patch: ItemPatch): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    if (patch.title !== undefined) {
      let t = y.get('title')
      if (!(t instanceof Y.Text)) {
        t = new Y.Text()
        y.set('title', t)
      }
      applyTextDiff(t as Y.Text, patch.title)
    }
    if (patch.done !== undefined) {
      if (patch.done === null) setCheckbox(ctx, id, false)
      else setDone(ctx, id, patch.done)
    }
    if (patch.assignee !== undefined) setKey(y, 'assignee', patch.assignee ?? undefined)
    if (patch.pinned !== undefined) setKey(y, 'pinned', patch.pinned || undefined)
    if (patch.page !== undefined) setKey(y, 'page', patch.page || undefined)
    if (patch.doneStyle !== undefined) setKey(y, 'doneStyle', patch.doneStyle === 'collapse' ? undefined : patch.doneStyle)
    if (patch.tint !== undefined) setKey(y, 'tint', patch.tint === 'none' ? undefined : patch.tint)
    if (patch.icon !== undefined) setKey(y, 'icon', cleanIcon(patch.icon))
    if (patch.coverId !== undefined) setKey(y, 'coverId', patch.coverId ?? undefined)
    if (patch.shareToken !== undefined) setKey(y, 'shareToken', patch.shareToken ?? undefined)
    if (patch.childFields !== undefined) setKey(y, 'childFields', patch.childFields?.length ? patch.childFields : undefined)
    if (patch.rowFields !== undefined) setKey(y, 'rowFields', patch.rowFields ?? undefined)
    if (patch.pageOrder !== undefined) setKey(y, 'pageOrder', patch.pageOrder?.length ? patch.pageOrder : undefined)
    if (patch.childPageOrder !== undefined) setKey(y, 'childPageOrder', patch.childPageOrder?.length ? patch.childPageOrder : undefined)
    if (patch.labels !== undefined) setKey(y, 'labels', patch.labels && Object.keys(patch.labels).length ? patch.labels : undefined)
    if (patch.link !== undefined) setKey(y, 'link', patch.link ?? undefined)
    if (patch.tags !== undefined) setTags(y, patch.tags)
    if (patch.addTags?.length) {
      const m = ensureTags(y)
      for (const t of patch.addTags) {
        const n = normalizeTag(t)
        if (n && !m.has(n)) m.set(n, true)
      }
    }
    if (patch.removeTags?.length) {
      const m = ensureTags(y)
      for (const t of patch.removeTags) m.delete(normalizeTag(t))
    }
    if (patch.props) for (const [k, v] of Object.entries(patch.props)) setProp(y, k, v)
    if (patch.calcInside) {
      const cur = { ...((y.get('calcInside') as Record<string, string> | undefined) ?? {}) }
      for (const [k, v] of Object.entries(patch.calcInside)) {
        if (v) cur[k] = v
        else delete cur[k]
      }
      setKey(y, 'calcInside', Object.keys(cur).length ? cur : undefined)
    }
    if (patch.total !== undefined) setKey(y, 'total', patch.total ?? undefined)
    touch(y, ctx.actor, nowOf(ctx))
  })
}

export function setTitle(ctx: OpContext, id: string, title: string): void {
  patchItem(ctx, id, { title })
}

export function putAttachment(ctx: OpContext, id: string, att: Attachment): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    ensureAttachments(y).set(att.id, att)
    touch(y, ctx.actor, nowOf(ctx))
  })
}

export function updateAttachment(ctx: OpContext, id: string, attId: string, patch: Partial<Attachment>): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    const m = ensureAttachments(y)
    const cur = m.get(attId)
    if (!cur) return
    m.set(attId, { ...cur, ...patch })
    touch(y, ctx.actor, nowOf(ctx))
  })
}

export function removeAttachment(ctx: OpContext, id: string, attId: string): void {
  tx(ctx, () => {
    const y = must(ctx, id)
    ensureAttachments(y).delete(attId)
    if (y.get('coverId') === attId) y.delete('coverId')
    touch(y, ctx.actor, nowOf(ctx))
  })
}

/**
 * Equations reading a tag group follow it to its new name (`plus` → `plus-ones`): on details,
 * on cards for everything inside, and in notes' lines (SPEC §7.12).
 */
export function retagEquations(ctx: OpContext, from: string, to: string): number {
  const tagRe = new RegExp(`#${from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=[/)\\s]|$)`, 'g')
  const map = (r: Ref): Ref => (r.k === 't' && r.group === from ? { ...r, group: to } : r)
  // References by id, and a #detail after where (`sum(Cost where #picked/yes)`).
  const remap = (e: string) => remapStored(e, map).replace(tagRe, `#${to}`)
  let n = 0
  tx(ctx, () => {
    itemsMap(ctx.doc).forEach((y) => {
      if (!(y instanceof Y.Map)) return
      let touched = false
      const props = yProps(y)
      props?.forEach((v, k) => {
        if (!isEq(v) || (!v.eq.includes(`{t:${from}`) && !v.eq.includes(`#${from}`))) return
        props.set(k, { eq: remap(v.eq) })
        touched = true
      })
      const rules = y.get('calcInside') as Record<string, string> | undefined
      if (rules && Object.values(rules).some((e) => e.includes(`{t:${from}`) || e.includes(`#${from}`))) {
        setKey(y, 'calcInside', Object.fromEntries(Object.entries(rules).map(([k, e]) => [k, remap(e)])))
        touched = true
      }
      for (const el of yBody(y)?.toArray() ?? []) {
        if (!(el instanceof Y.XmlElement) || el.nodeName !== 'calc') continue
        const e = el.getAttribute('expr')
        if (typeof e === 'string' && (e.includes(`{t:${from}`) || e.includes(`#${from}`))) {
          el.setAttribute('expr', remap(e))
          touched = true
        }
      }
      if (touched) {
        touch(y, ctx.actor, nowOf(ctx))
        n++
      }
    })
  })
  return n
}

/**
 * A card's total (SPEC §7.12): a detail on it adding up `of` (a field's key, or `#group` for a
 * tag group ending in numbers) over what's inside — everything, what's open, or what's done —
 * shown on its bar. Its key ("Cost total"), made if new. null takes it off. Returns the key.
 */
export function setCardTotal(ctx: OpContext, id: string, of: string | null, adds: 'all' | 'open' | 'done' = 'all'): string | null {
  return tx(ctx, () => {
    const y = must(ctx, id)
    const cur = y.get('total') as string | undefined
    const props = ensureProps(y)
    if (of === null) {
      if (cur && isEq(props.get(cur))) props.delete(cur)
      setKey(y, 'total', undefined)
      touch(y, ctx.actor, nowOf(ctx))
      return null
    }
    const fields = fieldsMap(ctx.doc)
    const def = fields.get(of) as FieldDef | undefined
    const group = of.startsWith('#') ? of.slice(1) : null
    if (!def && !group) throw new OpError('not_found', `No detail ${of}`)
    const label = def?.label ?? tagWords(group!)
    const ref = def ? `{f:${of}|${label}}` : `{t:${group}|${label}}`
    const stored = `sum(${ref}${adds === 'all' ? '' : ` where ${adds}`})`
    // The same total kept if there is one; else a new detail, "Cost total".
    const key = cur && isEq(props.get(cur)) ? cur : fieldKeyFromLabel(`${label} total`, fields.keys())
    const have = fields.get(key) as FieldDef | undefined
    if (!have) fields.set(key, { key, type: def?.type === 'money' ? 'money' : 'number', label: `${label} total` })
    props.set(key, { eq: stored })
    setKey(y, 'total', key)
    touch(y, ctx.actor, nowOf(ctx))
    return key
  })
}

const tagWords = (g: string) => {
  const s = g.replace(/[-_]+/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}
